// DocumentThumbnail.js — contenu de la zone d'aperçu d'un document.
//
// Affiche une vraie vignette image :
//   - image       → le fichier lui-même
//   - pdf/mov/html → la vignette `{id}_thumb.webp` générée au backend
//   - repli        → icône typée (si la vignette manque ou échoue à charger)
//
// Réutilisé par DocumentsTab (fiche joueur) et AllDocumentsTable (vue globale).
// Le composant rend uniquement le média : la zone (hauteur, clic, overlay) reste
// gérée par le parent.

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import CodeIcon from '@mui/icons-material/Code';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { isImage, isPdf, isVideo, isHtml } from '../utils/documentTypes';

const fileUrl = (filename) => `${getMediaBaseUrl()}/documents/${filename}?${new Date().getTime()}`;
const thumbUrl = (id) => `${getMediaBaseUrl()}/documents/${id}_thumb.webp`;

const IconFallback = ({ icon: Icon, color, label, iconSize }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Icon sx={{ fontSize: iconSize, color }} />
    <Typography variant="caption" display="block" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const DocumentThumbnail = ({ doc, iconSize = 48 }) => {
  const [thumbFailed, setThumbFailed] = useState(false);
  const ft = doc.file_type;
  const alt = doc.titre || doc.original_name;

  // Image : on affiche directement le fichier.
  if (isImage(ft)) {
    return (
      <Box
        component="img"
        src={fileUrl(doc.filename)}
        alt={alt}
        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  }

  // PDF / MOV / HTML : vignette générée, repli sur l'icône si absente/erreur.
  const previewable = isPdf(ft) || isVideo(ft) || isHtml(ft);
  if (previewable && !thumbFailed) {
    return (
      <Box
        component="img"
        src={thumbUrl(doc.id)}
        alt={alt}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
        onError={() => setThumbFailed(true)}
      />
    );
  }

  if (isPdf(ft)) {
    return <IconFallback icon={PictureAsPdfIcon} color="#f44336" label="PDF" iconSize={iconSize} />;
  }
  if (isVideo(ft)) {
    return <IconFallback icon={MovieIcon} color="#1e293b" label="MOV" iconSize={iconSize} />;
  }
  if (isHtml(ft)) {
    return <IconFallback icon={CodeIcon} color="#e67e22" label="HTML" iconSize={iconSize} />;
  }
  return <ImageIcon sx={{ fontSize: iconSize, color: 'grey.400' }} />;
};

export default DocumentThumbnail;
